import Section from '../../../components/Main/Section/Section';
import styled from 'styled-components';
import Filter from '../../../components/Filter/Filter';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Chip from '../../../components/Chip/Chip';
import SliderStarsAsLabels from '../../../components/Sliders/StarsAsLabels/SliderStarsAsLabels';
import PlatformSelect from '../../../components/PlatformSelect/PlatformSelect';
import { IPlatform } from '../../../shared/interfaces/IPlatform';
import PlatformService from '../../../services/PlatformService';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { Add, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import RightContainer from '../../../components/Containers/RightContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import { IMonitoring } from '../../../shared/interfaces/IMonitoring';
import ReviewMonitoringService from '../../../services/ReviewMonitoringService';
import Platform from '../../../components/Platform/Platform';
import ScoreService from '../../../services/ScoreService';
import { IScoreEntry } from '../../../shared/interfaces/IScoreEntry';
import { AuthContext } from '../../../providers/AuthProvider';
import MonitoringNoContent from '../../../assets/images/monitoring_no_content.png';
import NumberUtils from '../../../shared/NumberUtils';

const AddEditContainer = styled.div`
  margin-left: auto;
`;

const PlatformContainer = styled.div``;

interface IMonitoringLabelsWithCompanyNames {
  label: string;
  name: string;
  platformId: number;
}

const Platforms = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [monitoring, setMonitoring] = useState<IMonitoring[]>([]);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [monitoredPlatforms, setMonitoredPlatforms] = useState<IMonitoring[]>([]);
  const [lastTotalScores, setLastTotalScores] = useState<IScoreEntry[]>([]);
  const [monitoringLabelsWithCompanyNames, setMonitoringLabelsWithCompanyNames] = useState<
    Set<IMonitoringLabelsWithCompanyNames>
  >(new Set([]));

  const [loading, setLoading] = useState(false);

  const [sort, setSort] = useState('');

  const [filterByCompanyName, setFilterByCompanyName] = useState<string>('');
  const [filterStars, setFilterStars] = useState<number[]>([1, 5]);
  const [filterByPlatform, setFilterByPlatform] = useState<string>('');
  const [filterByType, setFilterByType] = useState<MonitoringFilterType>('all');
  const [showZeroReviews, setShowZeroReviews] = useState<boolean>(false);

  const filterByTypeLabel = {
    own: t('own'),
    benchmarking: t('benchmarking'),
    all: t('all_records'),
  }[filterByType];

  const handleFilterByCompanyNameChange = (event: SelectChangeEvent) => {
    setFilterByCompanyName(event.target.value);
  };

  const handleFilterStarsChange = (event: Event, newValue: number | number[]) => {
    setShowZeroReviews(true);
    setFilterStars(newValue as number[]);
  };

  const getRecordCount = () => {
    if (!filterByPlatform) {
      return `${platforms.length} ${platforms.length === 1 ? t('platform') : t('platforms')} - ${
        filteredPlatforms.length
      } ${filteredPlatforms.length === 1 ? t('entry') : t('entries')}`;
    } else {
      return `${filteredPlatforms.length} ${
        filteredPlatforms.length === 1 ? t('entry') : t('entries')
      }`;
    }
  };

  const getTotalScoreAndReviewCount = useCallback(
    (monitoringEntry: IMonitoring) => {
      let score: number;
      let reviewCount: number;
      let growthPercentage: number;
      let created: string;
      let employeeReviewCount: number;
      let applicantReviewCount: number;

      const matches = (kind: string) => {
        return lastTotalScores.filter(
          (company) =>
            company.company === monitoringEntry.company &&
            company.kind === kind &&
            (company.platform ||
              PlatformService.getPlatformName(monitoringEntry.platformId, platforms)) ===
              PlatformService.getPlatformName(monitoringEntry.platformId, platforms)
        );
      };

      score = matches('total')[0]?.score.score;
      reviewCount = matches('total')[0]?.score.reviewCount;
      growthPercentage = matches('total')[0]?.growthPercentage;
      created = matches('total')[0]?.createdAt;

      employeeReviewCount = matches('employee')[0]?.score.reviewCount;
      applicantReviewCount = matches('applicant')[0]?.score.reviewCount;

      return {
        score,
        reviewCount,
        growthPercentage,
        employeeReviewCount,
        applicantReviewCount,
        created,
      };
    },
    [lastTotalScores, platforms]
  );

  useEffect(() => {
    setCurrentView(t('nav_platforms'));
    document.title = `Reputativ ${t('nav_platforms')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);

      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };

    const getMonitoring = async () => {
      const data = await ReviewMonitoringService.getMonitoring(
        true,
        'both',
        setLoading,
        controller.signal
      );

      if (!data || 'success' in data) return;

      const monitoringEntries: IMonitoring[] = data;

      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitoringEntries.find((m) => m.platformId === v.id));
      });
      setMonitoringLabelsWithCompanyNames(
        new Set<IMonitoringLabelsWithCompanyNames>(
          monitoringEntries.map((entry) => {
            return { label: entry.label, name: entry.company, platformId: entry.platformId };
          })
        )
      );
      setMonitoring(monitoringEntries);
      setMonitoredPlatforms(monitoringEntries);
    };

    (async () => {
      await getPlatforms();
      await getMonitoring();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const getLastTotalScores = async (monitoring: IMonitoring[]) => {
      let scoreEntries: IScoreEntry[] = [];
      for (const value of monitoring) {
        const platformName = PlatformService.getPlatformName(value.platformId, platforms);
        const lastScores = await ScoreService.getLastScores(
          platformName,
          value.company,
          setLoading,
          controller.signal
        );

        if (!Array.isArray(lastScores)) return;
        scoreEntries = [...scoreEntries, ...lastScores];
      }
      setLastTotalScores(scoreEntries);
    };

    if (monitoring.length > 0) {
      (async () => await getLastTotalScores(monitoring))();
    }

    return () => {
      controller.abort();
    };
  }, [monitoring, platforms]);

  useEffect(() => {
    if (sort === '') {
      setMonitoredPlatforms(monitoring);
    }
    if (sort === '1') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) =>
          PlatformService.getPlatformName(a.platformId, platforms).toLowerCase() >
          PlatformService.getPlatformName(b.platformId, platforms).toLowerCase()
            ? 1
            : -1
        );
      });
    } else if (sort === '2') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) =>
          PlatformService.getPlatformName(a.platformId, platforms).toLowerCase() <
          PlatformService.getPlatformName(b.platformId, platforms).toLowerCase()
            ? 1
            : -1
        );
      });
    } else if (sort === '3') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { score: scoreA } = getTotalScoreAndReviewCount(a);
          const { score: scoreB } = getTotalScoreAndReviewCount(b);

          return scoreA > scoreB ? 1 : -1;
        });
      });
    } else if (sort === '4') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { score: scoreA } = getTotalScoreAndReviewCount(a);
          const { score: scoreB } = getTotalScoreAndReviewCount(b);

          return scoreA < scoreB ? 1 : -1;
        });
      });
    } else if (sort === '5') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { reviewCount: reviewCountA } = getTotalScoreAndReviewCount(a);
          const { reviewCount: reviewCountB } = getTotalScoreAndReviewCount(b);

          return reviewCountA > reviewCountB ? 1 : -1;
        });
      });
    } else if (sort === '6') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { reviewCount: reviewCountA } = getTotalScoreAndReviewCount(a);
          const { reviewCount: reviewCountB } = getTotalScoreAndReviewCount(b);

          return reviewCountA < reviewCountB ? 1 : -1;
        });
      });
    } else if (sort === '7') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { growthPercentage: growthPercentageA } = getTotalScoreAndReviewCount(a);
          const { growthPercentage: growthPercentageB } = getTotalScoreAndReviewCount(b);

          return growthPercentageA > growthPercentageB ? 1 : -1;
        });
      });
    } else if (sort === '8') {
      setMonitoredPlatforms((prevState) => {
        return [...prevState].sort((a, b) => {
          const { growthPercentage: growthPercentageA } = getTotalScoreAndReviewCount(a);
          const { growthPercentage: growthPercentageB } = getTotalScoreAndReviewCount(b);

          return growthPercentageA < growthPercentageB ? 1 : -1;
        });
      });
    }
  }, [sort, monitoring, platforms, getTotalScoreAndReviewCount]);

  const filteredPlatforms = monitoredPlatforms
    .filter((input, index) => {
      return (
        filterByCompanyName === '' || filterByCompanyName === `${input.company}_${input.platformId}`
      );
    })
    .filter((input) => {
      const platformLabel = PlatformService.getPlatformName(input.platformId, platforms);
      return filterByPlatform === platformLabel || filterByPlatform === '';
    })
    .filter((input) => {
      const { score } = getTotalScoreAndReviewCount(input);
      if (filterStars[0] === 1 && filterStars[1] === 5) {
        return true;
      }
      if (!showZeroReviews) {
        return score > 0;
      }
      return score >= filterStars[0] && score <= filterStars[1];
    })
    .filter((input) => {
      if (filterByType === 'all') {
        return true;
      } else if (filterByType === 'own') {
        return !input.isBenchmark;
      } else {
        return input.isBenchmark;
      }
    });

  const renderedPlatforms = filteredPlatforms.map((monitoring) => {
    const platformName = PlatformService.getPlatformName(
      monitoring.platformId,
      platforms
    ).toLowerCase();
    let { score, reviewCount, growthPercentage, employeeReviewCount, applicantReviewCount } =
      getTotalScoreAndReviewCount(monitoring);

    return (
      <Platform
        key={monitoring.id}
        monitoringId={monitoring.id}
        id={monitoring.platformId}
        platform={platformName}
        companyUrl={monitoring.url}
        company={monitoring.company}
        label={monitoring.label}
        score={score || 0}
        applicantReviews={applicantReviewCount}
        employeeReviews={employeeReviewCount}
        reviews={reviewCount}
        growth={growthPercentage}
        isBenchmark={monitoring.isBenchmark}
        createdAt={monitoring.createdAt}
        skipBackdated={
          PlatformService.getLocalPlatform(monitoring.platformId, platforms).skipBackdated
        }
        skipBackdatedAbove={
          PlatformService.getLocalPlatform(monitoring.platformId, platforms).skipBackdatedAbove
        }
      />
    );
  });

  return (
    <Section>
      <>
        {platforms.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <img
              width={300}
              src={MonitoringNoContent}
              alt="Monitoring no records"
            />
            <Typography sx={{ mt: theme.spacing(4) }}>{t('no_records')}</Typography>
            {hasRole('can-add') && !exceedsLimit('limitmonitoring', 1) && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('guide')}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('add_monitoring')}
              </Button>
            )}
          </Box>
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <ContentContainer>
                <LeftContainer>
                  <ActionBar
                    children={
                      <AddEditContainer>
                        {hasRole('can-edit') && (
                          <Button
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() => navigate('edit')}
                            sx={{ mr: theme.spacing(1) }}
                          >
                            {t('edit_monitoring')}
                          </Button>
                        )}
                        {hasRole('can-add') &&
                          !exceedsLimit('limitmonitoring', monitoredPlatforms.length + 1) && (
                            <Button
                              variant="contained"
                              startIcon={<Add />}
                              onClick={() => navigate('guide')}
                            >
                              {t('add_monitoring')}
                            </Button>
                          )}
                      </AddEditContainer>
                    }
                  />
                  <SortContainer sx={{ mb: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ my: theme.spacing(2) }}
                    >
                      {getRecordCount()}
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{ minWidth: '284px' }}
                    >
                      <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                      <Select
                        labelId="sort-select-label"
                        value={sort}
                        onChange={(event: SelectChangeEvent) => {
                          setSort(event.target.value);
                        }}
                        label={t('sort')}
                      >
                        <MenuItem value="">
                          <em>{t('unsorted')}</em>
                        </MenuItem>
                        <MenuItem value="1">{t('platforms_asc')}</MenuItem>
                        <MenuItem value="2">{t('platforms_desc')}</MenuItem>
                        <MenuItem value="3">{t('score_asc')}</MenuItem>
                        <MenuItem value="4">{t('score_desc')}</MenuItem>
                        <MenuItem value="5">{t('review_count_asc')}</MenuItem>
                        <MenuItem value="6">{t('review_count_desc')}</MenuItem>
                        <MenuItem value="7">{t('growth_percentage_asc')}</MenuItem>
                        <MenuItem value="8">{t('growth_percentage_desc')}</MenuItem>
                      </Select>
                    </FormControl>
                  </SortContainer>
                  {filterByType === 'all' && (
                    <Box
                      sx={{
                        display: 'flex',
                        mb: theme.spacing(2),
                      }}
                    >
                      <Box
                        sx={{
                          width: '6px',
                          height: '100%',
                          background: theme.palette.primary.main,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ ml: theme.spacing(1) }}
                      >
                        {t('own')}
                      </Typography>
                      <Box
                        sx={{
                          width: '6px',
                          height: '100%',
                          background: theme.palette.secondary.main,
                          ml: theme.spacing(2),
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ ml: theme.spacing(1) }}
                      >
                        {t('benchmarking')}
                      </Typography>
                    </Box>
                  )}
                  <PlatformContainer>{renderedPlatforms}</PlatformContainer>
                </LeftContainer>
                <RightContainer>
                  <Filter
                    filters={
                      <>
                        <FormControl
                          size="small"
                          sx={{ minWidth: '284px', mt: theme.spacing(2), mb: theme.spacing(1) }}
                        >
                          <InputLabel id="monitoring-label-filter-label">
                            {t('monitoring_label')}
                          </InputLabel>
                          <Select
                            labelId="monitoring-label-filter-label"
                            value={filterByCompanyName}
                            onChange={handleFilterByCompanyNameChange}
                            label={t('monitoring_label')}
                          >
                            <MenuItem value="">
                              <em>-</em>
                            </MenuItem>
                            {Array.from(monitoringLabelsWithCompanyNames).map(
                              ({ label, name, platformId }, index) => {
                                return (
                                  <MenuItem
                                    key={index}
                                    value={`${name}_${platformId}`}
                                  >
                                    {label}
                                  </MenuItem>
                                );
                              }
                            )}
                          </Select>
                        </FormControl>
                        <PlatformSelect
                          value={filterByPlatform}
                          onChange={(event: SelectChangeEvent) => {
                            setFilterByPlatform(event.target.value);
                          }}
                          platforms={platforms}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: 'uppercase',
                            fontWeight: '700',
                            mt: theme.spacing(1),
                          }}
                        >
                          {t('score')}
                        </Typography>
                        <SliderStarsAsLabels
                          value={filterStars}
                          onChange={handleFilterStarsChange}
                          sx={{ pb: theme.spacing(2) }}
                        />
                        <FormControl
                          size="small"
                          sx={{ minWidth: '284px', mt: theme.spacing(2), mb: theme.spacing(1) }}
                        >
                          <InputLabel id="benchmarking-filter-select-label">
                            {t('record_type')}
                          </InputLabel>
                          <Select
                            labelId="benchmarking-filter-select-label"
                            value={filterByType}
                            onChange={(event: SelectChangeEvent) => {
                              setFilterByType(event.target.value as MonitoringFilterType);
                            }}
                            label={t('record_type')}
                          >
                            <MenuItem value="all">{t('all_records')}</MenuItem>
                            <MenuItem value="own">{t('own')}</MenuItem>
                            <MenuItem value="benchmarking">{t('benchmarking')}</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    }
                    activeFilters={
                      <>
                        {(filterStars[0] !== 1 || filterStars[1] !== 5) && (
                          <Chip
                            label={`${t('stars')} ${filterStars[0]} - ${filterStars[1]}`}
                            onDelete={() => setFilterStars([1, 5])}
                          />
                        )}
                        {filterByPlatform !== '' && (
                          <Chip
                            label={filterByPlatform}
                            onDelete={() => {
                              setFilterByPlatform('');
                            }}
                          />
                        )}
                        {filterByType !== 'all' ? (
                          <Chip
                            label={filterByTypeLabel}
                            onDelete={() => {
                              setFilterByType('all');
                            }}
                          />
                        ) : (
                          <Chip label={filterByTypeLabel} />
                        )}
                      </>
                    }
                    resetActiveFilters={() => {
                      setFilterStars([1, 5]);
                      setFilterByPlatform('');
                      setFilterByType('all');
                    }}
                  />
                </RightContainer>
              </ContentContainer>
            )}
          </>
        )}
      </>
    </Section>
  );
};

export default Platforms;
